/**
 * Simnopsis-HX API client.
 *
 * Connects the Simnopsis-UI frontend to the Simnopsis-HX backend
 * for real audio processing (transcription + highlight detection + clip slicing).
 */

const API_BASE = (
  process.env.REACT_APP_SIMNOPSIS_HX_API ||
  'https://simnopsis-hx-production.up.railway.app'
).replace(/\/$/, '');

/**
 * Upload audio file and start extraction pipeline.
 * @param {File} file - Audio/video file
 * @param {object} opts - { mode, focus, tiers }
 * @returns {{ run_id: string, status: string }}
 */
export async function extractFromFile(file, { mode = 'best', focus = '', tiers = 'L,S,P' } = {}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', mode);
  formData.append('focus', focus);
  formData.append('tiers', tiers);

  const res = await fetch(`${API_BASE}/v1/extract`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Upload failed (${res.status})`);
  }

  return res.json();
}

/**
 * Extract highlights from a URL (YouTube, podcast, direct link).
 * @param {string} audioUrl
 * @param {object} opts - { mode, focus, tiers }
 * @returns {{ run_id: string, status: string }}
 */
export async function extractFromUrl(audioUrl, { mode = 'best', focus = '', tiers = 'L,S,P' } = {}) {
  const res = await fetch(`${API_BASE}/v1/extract/url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ audio_url: audioUrl, mode, focus, tiers }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `URL extraction failed (${res.status})`);
  }

  return res.json();
}

/**
 * Poll run status until complete or failed.
 * @param {string} runId
 * @param {function} onProgress - Callback with run data on each poll
 * @param {number} intervalMs - Poll interval (default 2000ms)
 * @returns {Promise<object>} - Final run data with highlights + clips
 */
export async function pollRun(runId, onProgress = null, intervalMs = 2000) {
  while (true) {
    const res = await fetch(`${API_BASE}/v1/runs/${runId}`);
    if (!res.ok) {
      throw new Error(`Failed to fetch run status (${res.status})`);
    }

    const run = await res.json();

    if (onProgress) onProgress(run);

    if (run.status === 'complete') return run;
    if (run.status === 'failed') throw new Error(run.error || 'Extraction failed');

    await new Promise(r => setTimeout(r, intervalMs));
  }
}

/**
 * Get highlights for a completed run.
 * @param {string} runId
 * @returns {{ highlights: Array }}
 */
export async function getHighlights(runId) {
  const res = await fetch(`${API_BASE}/v1/runs/${runId}/highlights`);
  if (!res.ok) throw new Error(`Failed to fetch highlights (${res.status})`);
  return res.json();
}

/**
 * Get clip audio URL (for <audio> src or download).
 * @param {string} clipId
 * @returns {string}
 */
export function getClipUrl(clipId) {
  return `${API_BASE}/v1/clips/${clipId}`;
}

/**
 * Get waveform bar data for a clip.
 * @param {string} clipId
 * @returns {{ bars: number[], duration_ms: number }}
 */
export async function getClipWaveform(clipId) {
  const res = await fetch(`${API_BASE}/v1/clips/${clipId}/waveform`);
  if (!res.ok) throw new Error(`Failed to fetch waveform (${res.status})`);
  return res.json();
}

/**
 * Generate short card titles via dual editorial keys (summary + title).
 * @param {Array<{id: string, text: string}>} items
 * @returns {Promise<{ titles: Array<{id: string, title: string, summary?: string, keys?: object}>, process?: object }>}
 */
export async function generateHighlightTitles(items) {
  const res = await fetch(`${API_BASE}/v1/titles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Title generation failed (${res.status})`);
  }
  return res.json();
}

/**
 * Check Simnopsis-HX health.
 * @returns {{ status, service, simscribe_x_url, simscribe_x_configured }}
 */
export async function checkHealth() {
  const res = await fetch(`${API_BASE}/health`);
  if (!res.ok) throw new Error('Simnopsis-HX unavailable');
  return res.json();
}
